import { Component, computed, forwardRef, input, model, viewChild, ChangeDetectionStrategy, signal } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { DxButtonGroupComponent, DxButtonGroupModule } from 'devextreme-angular';
import { ItemClickEvent } from 'devextreme/ui/button_group';
import { DECORATOR_HOST, DecoratorHost } from 'src/app/controls/directives/decorator-host';

@Component({
  selector: 'app-toggle-button',
  standalone: true,
  imports: [DxButtonGroupModule],
  templateUrl: './toggle-button.component.html',
  styleUrl: './toggle-button.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggleButtonComponent),
      multi: true
    },
    {
      provide: DECORATOR_HOST,
      useExisting: forwardRef(() => ToggleButtonComponent)
    }
  ],
})
export class ToggleButtonComponent implements ControlValueAccessor, DecoratorHost {
  private dx = viewChild.required<DxButtonGroupComponent>('dx');
  ngControl: NgControl = null;
  value = model<string[]>();
  multiSelect = input<boolean>(false);
  buttons = input.required<ToggleButtonItem[]>();

  private readonly inputId = signal<string | null>(null);
  protected readonly inputAttr = computed<Record<string, string>>(() => this.inputId() ? { id: this.inputId() } : {});

  selectionMode = computed(() => this.multiSelect() ? 'multiple' : 'single');

  get control(): AbstractControl {
    return this.ngControl?.control;
  }

  /*
  * ControlValueAccessor interface methods
  */

  writeValue(value: string[] | null): void {
    this.value.set(value);
  }

  onChange: (value: string[]) => void = () => { };
  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  onTouched: () => void = () => { };
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.dx().disabled = isDisabled;
  }

  computedValue = computed(() => this.value() || []);

  onInput(e: ItemClickEvent): void {
    const selectedKeys = this.dx().selectedItemKeys as string[];
    if (!this.arrayEquals(selectedKeys, this.computedValue())) {
      this.onChange(selectedKeys);
      this.value.set(selectedKeys);
    }
    e.component.repaint();
  }

  arrayEquals(a: string[], b: string[]): boolean {
    const array2Sorted = b.slice().sort();
    return a.length === b.length && a.slice().sort().every(function (value, index) {
      return value === array2Sorted[index];
    });
  }

  /*
   * DecoratorHost interface methods
   */

  setInputId(inputId: string | null): void {
    this.inputId.set(inputId);
  }

  getInputId(): string | null {
    return this.inputId();
  }
}

export class ToggleButtonItem {
  key: string;
  text: string;
  icon: string;
  hint: string;
}
